import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef, useLayoutEffect } from 'react';
import { Row, Col, Form, Checkbox, Divider, Input, Modal, Table } from 'antd';
import { Main, BasicFormWrapper, TableWrapper } from '../../styled';
import { Cards } from '../../../components/cards/frame/cards-frame';
import { AddUser, UserTableStyleWrapper } from '../style';
import Spinner from '../../common/Spinner.js';
import { Button } from '../../../components/buttons/buttons.js';
import { useParams, Link } from 'react-router-dom';
import TemplateConfig from './TemplateConfig.js';
import { useCMS } from '../../../utility/useCMS.js';
import Heading from '../../../components/heading/heading.js';
import Functions from '../../../utility/functions.js';
import { fetchEmailTemplates, fetchHostListing } from '../../../redux/manageSettings/actionCreator.js';
import { useDispatch } from 'react-redux';
import HttpStatus from '../../../utility/status.enum';

const TemplateDetail = forwardRef((props, ref) => {
  const { backEndPrefix, hostLoginRoleID, adminLoginRoleID, upsellMarkettingGroupId, smsMessage, emailMessage, channelMessage } = global;
  const smsTwilioValidationIndex = useRef({});
  const dispatch = useDispatch();

  const hostSlug = useParams()?.hostSlug ?? '';
  const textAreaRefs = useRef({});

  const updateProfileRef = useRef(null);
  const [form] = Form.useForm();
  const [isDetails, setIsDetails] = useState(true);
  const [loginUser, setLoginUser] = useState([]);
  const { fetchCMSCommonFunction } = useCMS();
  const [messageCMSMessage, setMessageCMSMessage] = useState('');
  const [schedulerTemplateList, setSchedulerTemplateList] = useState([]);
  const [isSchedulerEnabled, setIsSchedulerEnabled] = useState(true);
  const [showCmsPopupInfo, setShowCmsPopupInfo] = useState(false);
  const [isChannelPopupOpen, setIsChannelPopupOpen] = useState(false);
  const [isListingSelectionPopupOpen, setIsListingSelectionPopupOpen] = useState(false);
  const [listingLists, setListingLists] = useState([]);
  const [selectedListings, setSelectedListings] = useState({}); // Data structure of object to enlist all selected listings slug for particular message template
  const [sortedListingLists, setSortedListingLists] = useState([]);
  const [preListingData, setPreListingData] = useState({}); // Backup of previous listing selections for popup cancel/restore.
  const [spinnerLoader, setSpinnerLoader] = useState(false);
  const [additionalMessages, setAdditionalMessages] = useState([]);

  const [activeTab, setActiveTab] = useState({});
  const [originalMessages, setOriginalMessages] = useState([]);

  const [currentTemplateSlug, setCurrentTemplateSlug] = useState(null); // Selected slug (booking_confirmation, before_checkin, day_of_checkin_1 etc)

  // const maxTwilioCharacterLimit = 160;
  const [maxTwilioCharacterLimit, setMaxTwilioCharacterLimit] = useState(160);

  const isInitialLoading = spinnerLoader && schedulerTemplateList.length === 0;

  const canEdit =
    (hostLoginRoleID === loginUser.role_id && global.isScheduledEnabled) || adminLoginRoleID === loginUser.role_id;

  // Expose functions to parent
  useImperativeHandle(ref, () => ({
    updateIsDetails: () => {
      if (!isDetails) {
        if (updateProfileRef.current) {
          updateProfileRef.current.submitForm();
        }
      }
      setIsDetails(false); // parent can call this
    },
    // You can expose more functions if needed
  }));

  const handleProfileUpdate = () => {
    setIsDetails(false);
  };

  // Function you want to run from child
  const handleAfterProfileUpdate = async (shouldRefresh = false) => {
    if (shouldRefresh) {
      await fetchSchedulerList();
    }
    setIsDetails(true);
  };

  useEffect(() => {
    const getUser = JSON.parse(localStorage.getItem('user'));
    if (getUser) {
      setLoginUser(getUser);
    }
  }, [hostSlug]);

  // When user types manually sms
  const handleSMSChange = (index, value) => {
    form.setFieldValue(`sms_content_${index}`, value);
    setSchedulerTemplateList((prev) => {
      const updated = [...prev];
      updated[index].sms_content = value;
      return updated;
    });
  };

  const groupedTemplates = schedulerTemplateList?.length
    ? Object.entries(
      schedulerTemplateList.reduce((groups, template, index) => {
        const gid = template.group_id;
        if (!groups[gid]) groups[gid] = [];
        groups[gid].push({ template, index });
        return groups;
      }, {}),
    )
    : [];


  // Upsells Marketing info text
  const handleHelpPleasePopup = (event) => {
    event.preventDefault();
    const hasIntercomArticleId = Functions.findIntercomArticleId(messageCMSMessage?.upsell_help_text); // Check if CMS content has an Intercom ID
    if (!hasIntercomArticleId) {
      setShowCmsPopupInfo(true);
    }
  };

  // When user types manually email
  const handleEmailChange = (index, value) => {
    form.setFieldValue(`email_content_${index}`, value);
    setSchedulerTemplateList((prev) => {
      const updated = [...prev];
      updated[index].email_content = value;
      return updated;
    });
  };

  const handletab = (index, tab) => {
    setActiveTab((prev) => ({
      ...prev,
      [index]: tab,
    }));
  };

  useEffect(() => {
    if (!schedulerTemplateList?.length || !listingLists?.length) return;

    const allSlugs = listingLists.map((listing) => listing.slug);
    const defaultSelections = {};

    schedulerTemplateList.forEach((template) => {
      const templateSlug = template.slug;
      let collectedSlugs = [];

      if (selectedListings && Object.keys(selectedListings).length) {
        const existingSelection = selectedListings[templateSlug] || [];
        if (existingSelection.length) {
          collectedSlugs = existingSelection;
        }
      } else if (template.listing_slugs?.length) {
        collectedSlugs = template.listing_slugs;
      } else {
        collectedSlugs = allSlugs;
      }

      defaultSelections[templateSlug] = collectedSlugs;
    });

    setSelectedListings(defaultSelections);
  }, [schedulerTemplateList, listingLists]);

  // START: Listing Selection for template
  const openListingSelectionPopup = (templateSlug) => {
    if (!listingLists || listingLists.length === 0) return;
    /* Sorting START: Checked listing will appear topmost. */
    const selectedSlugs = new Set(selectedListings[templateSlug]);
    const selected = [];
    const notSelected = [];
    for (const item of listingLists) {
      (selectedSlugs.has(item.slug) ? selected : notSelected).push(item);
    }
    // If same length found then allow to update listingList state
    const listingListLen = selected.length + notSelected.length === listingLists.length;
    if (listingListLen) {
      setSortedListingLists([...selected, ...notSelected]); // sorted copy for popup display only
    }
    /* Sorting END */
    setCurrentTemplateSlug(templateSlug);
    setIsListingSelectionPopupOpen(true);
  };

  const closeListingSelectionPopup = () => {
    setIsListingSelectionPopupOpen(false);
  };

  const listingTableColumns = [
    {
      title: 'Listing',
      dataIndex: 'listing_name',
      key: 'listing_name',
    },
  ];

  const listingTableData = [];
  sortedListingLists?.map((listingData) => {
    const { slug } = listingData;
    const isChecked = selectedListings[currentTemplateSlug]?.includes(listingData.slug);
    return listingTableData.push({
      key: slug,
      slug,
      listing_name: (
        <div className="user-info">
          <div className="user-info-checkbox">
            <Form.Item
              className="mb-0"
              label={
                <span className="guest-ready-text">
                  <span></span>
                </span>
              }
              name="listing_slug"
              labelCol={{ className: 'custom-checkbox-label' }}
            >
              <Checkbox className="template-detail-view-checkbox" checked={isChecked} disabled></Checkbox>
            </Form.Item>
          </div>

          <figure>
            <img className="user-info-image" src={listingData?.listing_image} alt="" />
          </figure>
          <figcaption>
            <Heading className="user-name" as="h6">
              {global.checkPermission('listing-show') ? (
                <Link target="_blank" to={`${backEndPrefix}/listing/detail/${slug}`}>
                  <a className="boldurlLink">{listingData?.listing_name}</a>
                </Link>
              ) : (
                <span>{listingData?.listing_name}</span>
              )}
            </Heading>
            <span className="user-designation" />
            <span>
              <a href={listingData?.checkin_url_link} target="__blank" className="urlLink">
                {listingData?.checkin_url}
              </a>
            </span>
          </figcaption>
        </div>
      ),
    });
  });

  // START: Listing Additional Varilable Scope
  const fetchHostListings = async () => {
    setSpinnerLoader(true);
    try {
      const formData = { host_slug: hostSlug };
      const listingResponse = await dispatch(fetchHostListing(formData));
      if (listingResponse?.status === HttpStatus.HTTP_OK) {
        const listingData = listingResponse?.result || [];
        // Set Listing Data
        setListingLists(listingData);
        setSortedListingLists(listingData);
        setAdditionalMessages(listingData.map((listing) => listing.additional_message || ''));
        setOriginalMessages(listingData.map((listing) => listing.additional_message || ''));
      }
    } catch (error) {
      console.error('Error fetching Listings:', error);
    }
    setSpinnerLoader(false);
  };

  const fetchSchedulerList = async () => {
    setSpinnerLoader(true);
    setSelectedListings({}); // Reset stale selections before fetching fresh data
    try {
      const formData = { host_slug: hostSlug };
      const templateResponse = await dispatch(fetchEmailTemplates(formData));
      if (templateResponse?.status === HttpStatus.HTTP_OK) {
        const templateData = templateResponse?.result || [];
        const otherData = templateResponse?.other_data || [];
        const isSchedulerEnable = otherData?.is_scheduler_enable && otherData?.is_scheduler_enable === 1 ? true : false;
        if (otherData?.sms_charecter_limit) {
          setMaxTwilioCharacterLimit(otherData.sms_charecter_limit);
        }
        setIsSchedulerEnabled(isSchedulerEnable);

        // Set templates
        setSchedulerTemplateList(templateData);

        // Initialize form values
        const formValues = {};
        templateData.forEach((t, i) => {
          formValues[`note_${i}`] = t.content || '';
          formValues[`sms_content_${i}`] = t.sms_content || '';
          formValues[`email_content_${i}`] = t.email_content || '';
          formValues[`email_subject_${i}`] = t.email_subject || '';
          formValues[`days_${i}`] = t.days;
          formValues[`schedule_time_${i}`] = t.schedule_time;
          formValues[`minutes_${i}`] = t.minutes;
          formValues[`months_${i}`] = t.months;
          formValues[`sms_status_${i}`] = t.is_sms_enable; // Enable for group id upsell marketting.
          formValues[`email_status_${i}`] = t.is_email_enable; // Enable for group id upsell marketting.
        });
        form.setFieldsValue(formValues);

        //  Active slugs
        const initialTemplateSlugs = templateData.filter((item) => item.status_id).map((item) => item.slug);
        // fetching listings for host
        fetchHostListings();
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      fetchCMSCommonFunction(19)
        .then((description) => {
          setMessageCMSMessage(description);
        })
        .catch((error) => {
          console.error('Error fetching CMS message:', error);
        });
    }
    setSpinnerLoader(false);
  };

  useEffect(() => {
    const getUser = JSON.parse(localStorage.getItem('user'));
    if (getUser) {
      setLoginUser(getUser);
      // Then fetch after state reset is complete (slightly safer)
      // Using microtask queue ensures reset is done before fetching
      Promise.resolve().then(() => {
        fetchSchedulerList();
      });
    }
  }, [hostSlug]);


  const formatHourTo12 = (value) => {
    if (value === undefined || value === null) return '';

    // Extract hour if value is like "16:00"
    let hour = typeof value === 'string'
      ? parseInt(value.split(':')[0], 10)
      : value;

    if (isNaN(hour)) return '';

    let h = hour % 12;
    if (h === 0) h = 12;

    const period = hour < 12 ? 'AM' : 'PM';

    return `${h.toString().padStart(2, '0')}:00 ${period}`;
  };

  return (
    <>
      {isDetails ? (
        <>
          <AddUser>
            <BasicFormWrapper>
              <Form form={form} name="contact">
                <div headless className="host_userdetails_card">
                  <Spinner loader={spinnerLoader} />
                  <Row gutter={15} className="template-detail-root-row">
                    <Col xs={24} md={24}>
                      <div className="">
                        <div className="info">
                          {canEdit ? (
                            <>
                              {isInitialLoading ? (
                                <div className="template-detail-loading-wrap">
                                  <Cards
                                    xs={24}
                                    md={24}
                                    headless
                                    className="borderRadius common_fiter_table template-detail-loading-card"
                                  >
                                    <Main className="mainBlock messages-setting-module">
                                    </Main>
                                  </Cards>
                                </div>
                              ) : (
                                <>
                                  {/* Press the EDIT button to adjust your messages */}
                                  { isSchedulerEnabled ? groupedTemplates?.map(([groupId, items], groupIndex) => (
                                    <Cards
                                      key={groupId}
                                      xs={24}
                                      headless
                                      className={`borderRadius common_fiter_table ${groupId != 1 && isSchedulerEnabled ? 'borderRadius-24 mt-20 pt-24' : ''
                                        } `}
                                    >
                                      <Main className="mainBlock messages-setting-module" style={{ paddingBottom: '0px' }}>

                                        {/* SHOW TEMPLATES ONLY WHEN ENABLED */}
                                        {isSchedulerEnabled && items.length > 0 ? (
                                          <>
                                            <Col
                                              xs={24}
                                              sm={24}
                                              md={24}
                                              lg={16} // normal laptop
                                              xl={24} // 1280px
                                              xxl={16} // 1905px
                                              className='pl-0 pr-0'
                                            >
                                              <div className=" title-row-with-badge title-container">
                                                <div>
                                                  <Heading as="h3">
                                                    <span className="text-uppercase pl-5">{items[0].template.group_name}</span>
                                                  </Heading>
                                                </div>

                                                {groupIndex === 0 && (
                                                  <span className={`status-badge template-detail-status-badge ${isSchedulerEnabled ? "active" : "inactive"}`}>
                                                    {isSchedulerEnabled ? "Active" : "Inactive"}
                                                  </span>
                                                )}
                                              </div>
                                            </Col>
                                            {groupIndex === 0 && (<Divider className="title-divider" />)}
                                          
                                            {/* Upsells Marking Info Text */}
                                            {groupId == upsellMarkettingGroupId && (
                                              <div className="project-modal">
                                                <Col
                                                  xs={24}
                                                  sm={24}
                                                  md={24}
                                                  lg={16} // normal laptop
                                                  xl={24} // 1280px
                                                  xxl={16} // 1905px
                                                >
                                                  <span>
                                                    Automated Early Check-In Availability (if marked ready more than an hour before check-in
                                                    time){' '}
                                                    : <br />
                                                    - For same-day check-ins, guests receive this message about 3 minutes after marking
                                                    guest ready <br />- For non-same-day check-ins, the next guest will receive this message
                                                    at 9am on their check-in date
                                                  </span>
                                                </Col>
                                              </div>
                                            )}
                                              
                                            {items.map(({ template: schedulerTemplate, index }) => {
                                              const { config, title, group_id } = schedulerTemplate || {};
                                              const { days, time, minutes, months } = config || {};
                                              const [mainTitle, ...restTitle] = title?.split(':') || [];
                                              let subTitle = restTitle.length ? restTitle.join(':').trim() : null;
                                              if (months?.is_display && subTitle) {
                                                const monthsVariable = Number(form.getFieldValue(`months_${index}`) ?? 0);
                                                if (monthsVariable > 0) {
                                                  subTitle = subTitle.replace(/\bX\b(?=\s+months?\b)/i, monthsVariable);
                                                }
                                              }
                                              const smsStatus = form.getFieldValue(`sms_status_${index}`) ?? false;
                                              const needsToDisable = false; // Upsell Messaging
                                              const defaultTab = needsToDisable ? smsMessage : channelMessage;
                                              const currentActiveTab = activeTab[index] ?? defaultTab;
                                              const scheduleSubTitle = [
                                                days?.is_display ? `${schedulerTemplate?.days} ${days?.label}` : null,
                                                time?.is_display ? `${schedulerTemplate?.schedule_time ?? 0} ${time?.label}` : null, ,
                                                minutes?.is_display ? `${schedulerTemplate?.minutes ?? 0} ${minutes?.label}` : null,
                                                months?.is_display ? `${schedulerTemplate?.months ?? 0} ${months?.label}` : null,
                                              ]
                                                .filter(Boolean)
                                                .join(' ');
                                              const combinedSubTitle = [!needsToDisable ? scheduleSubTitle : null]
                                                .filter(Boolean)
                                                .join(' ');
                                              const statusMap = {
                                                [channelMessage]: schedulerTemplate?.is_channel_enable ?? false,
                                                [smsMessage]: schedulerTemplate?.is_sms_enable ?? false,
                                                [emailMessage]: schedulerTemplate?.is_email_enable ?? false,
                                              };
                                              const activeStatus =  Boolean(statusMap[currentActiveTab]);
                                              return (
                                                <React.Fragment key={index}>
                                                  <Col
                                                    xs={24}
                                                    sm={24}
                                                    md={24}
                                                    lg={16} // normal laptop
                                                    xl={24} // 1280px
                                                    xxl={16} // 1905px
                                                  >
                                                    <div className="scheduler-row-inline">
                                                      <div className="template-detail-title-row">
                                                        {title && (
                                                          <span className="scheduler-title-group">
                                                            <strong className="scheduler-main-title color-dark">
                                                              {!needsToDisable ? `#${index + 1} ${mainTitle}` : mainTitle}
                                                            </strong>
                                                          </span>
                                                        )}
                                                      </div>

                                                      <div className="template-detail-subtitle-row">
                                                        <div className="template-detail-subtitle-left">
                                                          {combinedSubTitle && <span className="scheduler-sub-title text-muted-small">⏱️{" "}{subTitle}{" "}{combinedSubTitle}</span>}
                                                        </div>
                                                        <div className="color-dark use-this-text select_listing_right template-detail-listing-right">
                                                          {!needsToDisable && schedulerTemplate?.listing?.total_listings > 0 && (
                                                            <>
                                                              {(() => {
                                                                const slugs = schedulerTemplate?.listing_slugs || [];
                                                                const total = schedulerTemplate?.listing?.total_listings || 0;

                                                                const n = slugs.length > 0 ? slugs.length : total;

                                                                return (
                                                                  <>
                                                                  <div className='listing-name-display'>
                                                                    <span className="font-14-700">
                                                                      {schedulerTemplate?.listing?.listing_name}
                                                                    </span>

                                                                    {n > 1 && (
                                                                      <>
                                                                        &nbsp;&amp;&nbsp;
                                                                        <a
                                                                          className="common_urlLink font-14-700"
                                                                          href="#"
                                                                          onClick={(e) => {
                                                                            e.preventDefault();
                                                                            openListingSelectionPopup(schedulerTemplate?.slug);
                                                                          }}
                                                                        >
                                                                          {`${n - 1} more`}
                                                                        </a>
                                                                      </>
                                                                    )}
                                                                    </div>
                                                                  </>
                                                                );
                                                              })()}
                                                            </>
                                                          )}
                                                        </div>
                                                      </div>

                                                      <Row gutter={15} key={index}>
                                                        <Col xl={24} xs={24} className="pl-0 pr-0">
                                                          <div className="custom-tabs-wrapper-detail project-modal">
                                                            <div className="custom-tabs template-detail-tabs-row">
                                                              {/* Hide Channel Tab for Upsell Marketting */}
                                                              <div className="custom-tab-wrapper">
                                                                {!needsToDisable && (
                                                                  <div
                                                                    className={`custom-pill ${currentActiveTab === channelMessage ? 'active' : ''
                                                                      }`}
                                                                    onClick={() => handletab(index, channelMessage)}
                                                                  >
                                                                    Channel
                                                                  </div>
                                                                )}

                                                                <div
                                                                  className={`custom-pill ${currentActiveTab === smsMessage
                                                                    ? 'active'
                                                                    : ''
                                                                    }`}
                                                                  onClick={() => handletab(index, smsMessage)}
                                                                >
                                                                  SMS
                                                                </div>

                                                                  <div
                                                                    className={`custom-pill ${currentActiveTab === emailMessage ? 'active' : ''
                                                                      }`}
                                                                    onClick={() => handletab(index, emailMessage)}
                                                                  >
                                                                    Email
                                                                  </div>
                                                              </div>
                                                              <span className={`status-badge template-detail-status-badge ${activeStatus ? "active" : "inactive"}`}>
                                                                {activeStatus ? "Active" : "Inactive"}
                                                              </span>
                                                            </div>
                                                          </div>
                                                        </Col>

                                                        {currentActiveTab === emailMessage && (
                                                          <Col xs={24} className="pl-0 pr-0">
                                                            <div className="project-modal provider_bodyvariable_box">
                                                              <Form.Item
                                                                className="email_subject_field_name"
                                                                name={`email_subject_${index}`}
                                                              >
                                                                <Input
                                                                  //  onChange={(e) => handleEmailSubject(index, e.target.value)}
                                                                  placeholder="Enter email subject"
                                                                  className="h-50"
                                                                />
                                                              </Form.Item>
                                                            </div>
                                                          </Col>
                                                        )}

                                                        {/* CHANNEL MESSAGE */}
                                                        {!needsToDisable && (
                                                          <Col
                                                            xs={24}
                                                            className={`pl-0 pr-0 ${currentActiveTab === channelMessage ? 'd-block' : 'd-none'
                                                              }`}
                                                          >
                                                            <div className="project-modal provider_bodyvariable_box message-variable-textarea message-variable-list message_detail_textarea">
                                                              <Form.Item
                                                                // label="Notes"
                                                                name={`note_${index}`}
                                                              >
                                                                <Input.TextArea
                                                                  ref={(el) => (textAreaRefs.current[`channel_${index}`] = el)}
                                                                  placeholder="Type your message to the guest here."
                                                                  autoSize={{ minRows: 2, maxRows: 100 }}
                                                                  id={`template_content_${index}`}
                                                                  disabled
                                                                />
                                                              </Form.Item>
                                                            </div>
                                                          </Col>
                                                        )}
                                                        {/* SMS MESSAGE */}
                                                        <Col
                                                          xs={24}
                                                          className={`pl-0 pr-0 ${currentActiveTab === smsMessage
                                                            ? 'd-block'
                                                            : 'd-none'
                                                            }`}
                                                        >
                                                          <div className="project-modal provider_bodyvariable_box message-variable-textarea message_detail_textarea">
                                                            <Form.Item
                                                              // label="Notes"
                                                              name={`sms_content_${index}`}
                                                            >
                                                              <Input.TextArea
                                                                ref={(el) => (textAreaRefs.current[`sms_${index}`] = el)}
                                                                placeholder="Short SMS message for the guest."
                                                                autoSize={{ minRows: 2, maxRows: 100 }}
                                                                id={`template_content_sms_${index}`}
                                                                onChange={(e) => handleSMSChange(index, e.target.value)}
                                                                disabled
                                                              />
                                                            </Form.Item>
                                                          </div>
                                                        </Col>
                                                        {/* EMAIL MESSAGE */}
                                                        <Col
                                                          xs={24}
                                                          className={`pl-0 pr-0 ${currentActiveTab === emailMessage ? 'd-block' : 'd-none'}`}
                                                        >
                                                          <div className="project-modal provider_bodyvariable_box message-variable-textarea message_detail_textarea">
                                                            <Form.Item
                                                              // label="Notes"
                                                              name={`email_content_${index}`}
                                                            >
                                                              <Input.TextArea
                                                                ref={(el) => (textAreaRefs.current[`email_${index}`] = el)}
                                                                placeholder="Write a detailed email for the guest."
                                                                autoSize={{ minRows: 2, maxRows: 100 }}
                                                                id={`template_content_email_${index}`}
                                                                onChange={(e) => handleEmailChange(index, e.target.value)}
                                                                disabled
                                                              />
                                                            </Form.Item>
                                                          </div>
                                                        </Col>
                                                        {/* If SMS Enable and length of sms more than twilio limit */}
                                                        {/* For disabled group id upsell marketing bypassing the validation. */}
                                                        {smsStatus && smsTwilioValidationIndex.current[index] && !needsToDisable && (
                                                          <div className="showImageError">
                                                            <div class="ant-form-item-has-error ant-form-item-explain-error">
                                                              SMS limit should be less than {maxTwilioCharacterLimit} character
                                                            </div>
                                                          </div>
                                                        )}
                                                      </Row>
                                                      <Divider />
                                                    </div>
                                                  </Col>
                                                </React.Fragment>
                                              );
                                            })}
                                          </>
                                        ) : loginUser.role_id === hostLoginRoleID ? (
                                          <>
                                          <div className="project-modal">
                                              <span
                                                className="custom_editor_span_text"
                                                dangerouslySetInnerHTML={{ __html: messageCMSMessage?.automated_setting_message }}
                                              />
                                            </div>
                                          </>
                                        ) : (
                                          <></>
                                        )}
                                      </Main>
                                    </Cards>
                                  )) : (
                                      <Cards xs={24} headless className="borderRadius common_fiter_table">
                                        <Main className="mainBlock">
                                          <Row gutter={15}>
                                            <Col xs={24} md={12}>
                                              <div className="project-modal">
                                                {messageCMSMessage?.edit_action_message ? (
                                                  <span
                                                    className="custom_editor_span_text"
                                                    dangerouslySetInnerHTML={{
                                                      __html: messageCMSMessage.edit_action_message,
                                                    }}
                                                  />
                                                ) :
                                                  <span>&emsp;</span>
                                                }
                                                </div>
                                            </Col>
                                          </Row>
                                        </Main>
                                      </Cards>
                                    )}
                                </>
                              )}
                            </>
                          ) : (
                              <>
                                <Cards xs={24} headless className="borderRadius common_fiter_table">
                                  <Main className="mainBlock ">
                                    <Row gutter={15}>
                                      <Col xs={24} md={12}>
                                        <div className="project-modal">
                                          {messageCMSMessage?.automated_setting_message ?
                                            <span
                                              className="custom_editor_span_text"
                                              dangerouslySetInnerHTML={{
                                                __html: messageCMSMessage?.automated_setting_message,
                                              }}
                                            /> :
                                            <span>&ensp;</span>
                                          }
                                        </div>
                                      </Col>
                                    </Row>
                                  </Main>
                                </Cards>
                              </>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Form>
            </BasicFormWrapper>
          </AddUser>
          {canEdit && !spinnerLoader && (
            <div className="manage-listing-btn">
              <Button
                htmlType="submit"
                outlined
                shape="circle"
                type="primary"
                className="w-100 edit-btn"
                onClick={handleProfileUpdate}
              >
                Edit
              </Button>
            </div>
          )}
        </>
      ) : (
        <TemplateConfig ref={updateProfileRef} onConfigUpdated={handleAfterProfileUpdate}></TemplateConfig>
      )}
      <Modal
        type="primary"
        open={isListingSelectionPopupOpen}
        footer={null}
        onCancel={closeListingSelectionPopup}
        width={'90%'}
        maskClosable={false}
        key={2}
        aria-hidden="false"
        className="bulk-listing-modal template-detail-listing-modal"
        title={`Selected Listings (${selectedListings[currentTemplateSlug]?.length || 0}/${listingLists?.length || 0})`}
      >
        <div className="guide-matching-listing">
          <div className="project-modal">
            <div className="guide-card">
              <div className="guide-card-body">
                <UserTableStyleWrapper>
                  <div className="contact-table">
                    <TableWrapper className="table-responsive">
                      <Table
                        dataSource={listingTableData}
                        columns={listingTableColumns}
                        rowKey="slug"
                        pagination={false}
                        className={`listing-no-thead-table`}
                      />
                    </TableWrapper>
                  </div>
                </UserTableStyleWrapper>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </>

  );
});

export default TemplateDetail;
